/**
 * Shared helpers for cleaning question labels and formatting answer payloads
 * in QA Review and Client Report views.
 *
 * Rules:
 *  - Strip ALL HTML tags from question labels (some surveys imported as rich
 *    text leak <p>, <strong>, <span style=...> tags into the QA UI).
 *  - Strip "(copy)" / "(copy 2)" / "(Copy 3)" trailing artefacts that appear
 *    when a survey was duplicated in the builder.
 *  - Yes/No display: tolerate every shape we've seen in production
 *      • { label: 'Yes', value: 'yes', score_value: 1, comment }
 *      • boolean true / false
 *      • 0 / 1 / '0' / '1' (truthy → Yes)
 *      • plain strings 'yes' / 'no' (case-insensitive)
 *  - NPS display: numeric 0–10 score; the wrapper {value, score_value, comment}
 *    is also supported.
 *  - Number display: bare number or { value, comment }.
 */

const COPY_RE = /\s*\((?:copy|Copy|COPY)(?:\s*\d+)?\)\s*$/g;

// Drop <script>/<style> block CONTENTS entirely — mirrors backend
// `utils/sanitize.strip_html`. Case-insensitive, spans newlines.
const BLOCK_CONTENT_RE = /<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;
// HTML comments.
const COMMENT_RE = /<!--[\s\S]*?-->/g;
// Any remaining tag (opening/closing/self-closing).
const TAG_RE = /<[^>]+>/g;

/**
 * Platform-wide plain-text renderer for author-written question / label /
 * sub-text strings that may contain pasted legacy Word/browser rich-text
 * markup (e.g. `<p class="p1" style="font-family:&quot;Helvetica Neue&quot;">`
 * or `<span class="Apple-converted-space">&nbsp;</span>`).
 *
 * Rules:
 *  1. Strip <script>/<style> block contents entirely (never leak code).
 *  2. Strip HTML comments.
 *  3. Strip every remaining tag (contents kept).
 *  4. Decode HTML entities — via a <textarea> parser when a DOM is
 *     available (handles the full spec incl. &#160;), with a small
 *     regex-based fallback for SSR / test contexts.
 *  5. Collapse runs of whitespace, drop trailing "(copy)"/"(copy 2)"
 *     artefacts from duplicated builder questions.
 *  6. Never execute HTML. Plain text out.
 *
 * Ordinary plain-text inputs (no tags, no entities) round-trip cleanly.
 */
export function cleanQuestionLabel(raw) {
  if (raw == null) return '';
  let s = String(raw);
  // 1. Kill script/style blocks (wrapper + contents) and comments.
  s = s.replace(BLOCK_CONTENT_RE, '').replace(COMMENT_RE, '');
  // 2. Strip remaining tags — contents preserved.
  s = s.replace(TAG_RE, '');
  // 3. Decode entities.
  if (typeof document !== 'undefined') {
    const el = document.createElement('textarea');
    el.innerHTML = s;
    s = el.value;
  } else {
    s = s
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&#([0-9]+);/g, (_, n) => String.fromCharCode(Number(n)));
  }
  // 4. Strip trailing (copy) / (copy 2) artefacts (can repeat).
  while (COPY_RE.test(s)) {
    s = s.replace(COPY_RE, '');
  }
  return s.replace(/\s+/g, ' ').trim();
}

/** Return the canonical question label for a builder question doc, after cleaning. */
export function getQuestionLabel(q) {
  if (!q) return '';
  const raw = q.question_text ?? q.text ?? q.label ?? q.title ?? '';
  return cleanQuestionLabel(raw);
}

/**
 * Normalise a Yes/No raw answer into one of: 'Yes' | 'No' | null.
 * Returns null when we cannot confidently determine the selection.
 *
 * IMPORTANT: this is display-layer only — we never mutate the stored answer.
 */
export function normaliseYesNoAnswer(raw) {
  if (raw == null) return null;
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  if (typeof raw === 'number') {
    if (raw === 1) return 'Yes';
    if (raw === 0) return 'No';
    return null;
  }
  if (typeof raw === 'string') {
    const s = raw.trim().toLowerCase();
    if (['yes', 'y', 'true', '1'].includes(s)) return 'Yes';
    if (['no', 'n', 'false', '0'].includes(s)) return 'No';
    if (['n/a', 'na', 'not applicable'].includes(s)) return 'N/A';
    return null;
  }
  if (typeof raw === 'object') {
    // Prefer an explicit label when present.
    if (typeof raw.label === 'string') {
      const lbl = raw.label.trim().toLowerCase();
      if (['yes', 'y'].includes(lbl)) return 'Yes';
      if (['no', 'n'].includes(lbl)) return 'No';
      if (['n/a', 'na', 'not applicable'].includes(lbl)) return 'N/A';
      // unknown label — return verbatim with title-case fallback
      return raw.label;
    }
    if (typeof raw.value === 'string' || typeof raw.value === 'boolean' || typeof raw.value === 'number') {
      return normaliseYesNoAnswer(raw.value);
    }
    if (typeof raw.score_value === 'number') {
      if (raw.score_value === 1) return 'Yes';
      if (raw.score_value === 0) return 'No';
    }
  }
  return null;
}

/** Pull the optional comment off a structured answer wrapper. */
export function answerComment(raw) {
  if (raw && typeof raw === 'object' && typeof raw.comment === 'string' && raw.comment.trim()) {
    return raw.comment.trim();
  }
  return '';
}

/**
 * Visibility gate for the QA-review comment textarea.
 *
 * Rules (Feb 2026):
 *   • RATING / NPS questions default `editTarget = 'comment'` → box is
 *     always shown (pre-existing behaviour; NOT changed here).
 *   • For CHOICE_TYPES (yes/no, single/multiple choice, dropdown,
 *     button_choice), we now consult the LIVE question config:
 *         q.commentable === true            → show the box (even when the
 *                                             answer has no comment yet,
 *                                             e.g. older responses submitted
 *                                             before the flag was flipped on).
 *         existing non-empty comment string → show the box (pre-existing).
 *   • Text/voice edits (`editTarget === 'text'`) never show the comment box.
 *
 * Kept as a pure function so it's unit-testable and reusable.
 */
export function shouldShowCommentBox({ editTarget, qtype, q, raw, baseText }) {
  if (editTarget === 'text') return false;
  if (NPS_TYPES.has(qtype)) return false; // NPS score is read-only in QA; reason belongs in follow-up question
  if (editTarget === 'comment') return true; // Rating comment
  if (CHOICE_TYPES.has(qtype)) {
    return Boolean(q?.commentable) || Boolean(baseText) || Boolean(answerComment(raw));
  }
  return false;
}

/** Pull the numeric NPS score (0–10) from a raw answer, or null. */
export function normaliseNpsAnswer(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string' && /^-?\d+(\.\d+)?$/.test(raw.trim())) return Number(raw);
  if (typeof raw === 'object') {
    if (typeof raw.score_value === 'number') return raw.score_value;
    if (typeof raw.value === 'number') return raw.value;
    if (typeof raw.value === 'string' && /^-?\d+(\.\d+)?$/.test(raw.value.trim())) return Number(raw.value);
  }
  return null;
}

/** Pull a numeric "number" answer (free-form) from a raw answer, or null. */
export function normaliseNumberAnswer(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s === '') return null;
    if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s);
    return s; // keep alpha-numeric (e.g. "1325 PHP") — caller decides
  }
  if (typeof raw === 'object') {
    if (typeof raw.value === 'number') return raw.value;
    if (typeof raw.value === 'string') return normaliseNumberAnswer(raw.value);
  }
  return null;
}

/** Format hours into the canonical "X.X hrs after visit" display. */
export function formatSubmissionWindow(hours) {
  if (hours == null || hours === '') return '—';
  const n = Number(hours);
  if (!Number.isFinite(n)) return String(hours);
  // Show one decimal when it isn't an integer, otherwise drop the trailing .0
  const pretty = Number.isInteger(n) ? `${n}` : n.toFixed(1);
  return `${pretty} hrs after visit`;
}

/** Question-type categorisation used by both QA + Report views. */
export const DISPLAY_ONLY_TYPES = new Set([
  'welcome', 'thank_you', 'section_header', 'rich_text_block',
  'image_display', 'page_break', 'confirmation',
]);

export const YES_NO_TYPES = new Set(['yes_no']);
export const CHOICE_TYPES = new Set([
  'yes_no', 'single_choice', 'multiple_choice', 'dropdown', 'button_choice',
]);
export const RATING_TYPES = new Set(['star_rating', 'rating_scale']);
export const NPS_TYPES = new Set(['nps']);
export const NUMBER_TYPES = new Set(['number', 'number_input']);
export const TEXT_TYPES = new Set(['short_text', 'long_text', 'text', 'voice', 'email']);

/** Resolve the canonical question type (tolerant of both naming conventions). */
export function getQuestionType(q) {
  return q?.question_type || q?.type || 'short_text';
}
